import { useState, useEffect, useMemo } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { Spinner, Button } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import styles from '../CustomDomainModal.module.css'
import Timer from './Timer'
import {
  autoConnectDomainFinish,
  autoConnectDomainAdd,
  publishSiteToCustomDomain
} from '../../../services/custom-domain'
import { domainValidateErrorMessage, domainErrorMessage } from '../../CustomDomainTable'
import { SUCCESS, VALIDATE_RECORDS, VALIDATE_RECORDS_DIAGNOSTICS } from '../constants/strings'
import DNSTable from './DNSTable'
import { API_ROOT } from 'constants'
import api from 'utils/api'
import { useCustomDomain } from '../context/CustomDomainProvider'
import CustomDomainSelectorModal from '../../CustomDomainSelectorModal/CustomDomainSelectorModal'
import ProjectSelectorModal from './ProjectSelectorModal'
import { showCreateSiteModal, setAction } from '../../../Manage/actions'
import { ACTION_CREATE } from '../../CreateUpdateSiteModal/interface'

const Success = ({
  formik,
  onPrevious,
  updateDomains,
  submit,
  onFail,
  onSelectEntri,
  refreshProfile,
  handleClose,
  setPublishSuccessData,
  onCAAError
}) => {
  const [loading, setLoading] = useState(false)
  const { isPublishToCustomDomain, setIsPublishToCustomDomain } = useCustomDomain()
  const { DNSRecordsDiagnostics, setDNSRecordsDiagnostics } = useCustomDomain()
  const { userProfile, customDomains } = useSelector((state) => state.manage)
  const [isDiagnosticsError, setIsDiagnosticsError] = useState(false)
  const [showProjectSelectorModal, setShowProjectSelectorModal] = useState(false)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const dispatch = useDispatch()
  const history = useHistory()
  const createdTime = localStorage.getItem('start_time')
  const validate = localStorage.getItem('validate')
  const retry = localStorage.getItem('retry')
  const viewRecords = localStorage.getItem('view_records')
  const { values } = formik
  const dnsData = values.dnsRecords.data
  const domain = values.dnsAddress.domain
  const subdomain = values.dnsAddress.subdomain
  const fulldomainAddress = !!subdomain ? `${subdomain}.${domain}` : domain

  // Get validated domains for the publish modal (filter to only include the newly created domain)
  const newlyCreatedDomain = customDomains?.find((c) => c.domain === domain)
  const validatedCustomDomains = newlyCreatedDomain ? [newlyCreatedDomain] : []

  const isDiagnosticsMismatch = (data) => data && !data?.success && data?.records?.length

  const handlePublishExistingProject = () => {
    setShowProjectSelectorModal(true)

    // Update URL to deeplink format
    const fullDomain = subdomain ? `${subdomain}.${domain}` : domain
    history.replace({
      pathname: '/manage/domain/update',
      search: `?site=${fullDomain}`
    })
  }

  const handleProjectSelected = (project) => {
    setShowProjectSelectorModal(false)
    setSelectedProject(project)
    setShowPublishModal(true)
  }

  const handleCloseProjectSelector = () => {
    setShowProjectSelectorModal(false)
    // Revert URL back to /manage
    history.replace({ pathname: '/manage' })
  }

  const handleUpload = () => {
    const customDomainSuffix = `.${domain}`
    const customSubdomain = subdomain || 'www'

    localStorage.setItem('active_custom_domain', customDomainSuffix)
    localStorage.setItem('active_custom_subdomain', customSubdomain)

    handleClose()

    dispatch(setAction(ACTION_CREATE))
    dispatch(showCreateSiteModal(true, null, ACTION_CREATE))
  }

  const updateSitePublishToCustomDomain = async (site) => {
    let splitSubdomain = site.publishToCustomDomain?.split('.')
    let subdomain = site.subdomain || splitSubdomain[0]
    let tld = `.${site.domain}`

    const formData = new FormData()
    formData.append('newDomain', `${subdomain}${tld}`)
    formData.append('publishToCustomDomain', site.publishToCustomDomain)

    setIsPublishToCustomDomain(true)
    return await publishSiteToCustomDomain({
      currentSiteDomain: site.publishToCustomDomain,
      formData
    })
  }

  const handleRevalidate = async () => {
    setLoading(true)
    setDNSRecordsDiagnostics(null)
    try {
      if (!!domain) {
        const result = await autoConnectDomainFinish({ domain })
        const {
          data: { domains, success }
        } = result
        if (success) {
          const siteToPublish = localStorage.getItem('publishToCustomDomain')
          if (siteToPublish) {
            const publishSite = domains.find((d) => d.domain === domain && d.publishToCustomDomain === siteToPublish)

            if (publishSite) {
              try {
                const response = await updateSitePublishToCustomDomain(publishSite)
                if (response.data) {
                  setPublishSuccessData({
                    site: response.data?.link || {},
                    files: response.data?.files || [],
                    userProfile: userProfile
                  })
                }
              } catch (error) {
                console.error('Failed to publish site to custom domain:', error)
              } finally {
                localStorage.removeItem('publishToCustomDomain')
                handleClose()
                return
              }
            }
          } else {
            updateDomains(domains)
            formik.setFieldValue(`isSetUpComplete`, true)
          }
          refreshProfile()
        } else if (!success && !!validate) {
          formik.setFieldValue(`validationFailed`, true)
        } else {
          onFail(domainValidateErrorMessage)
        }
      }
      setLoading(false)
    } catch (error) {
      const errorCode = error?.response?.data?.code
      if (errorCode === 'ACM_CERT_STATUS_FAILED' || errorCode === 'ACM_STATUS_FAILED') {
        setLoading(false)
        onCAAError?.({
          domain: fulldomainAddress,
          originalDomain: domain,
          subdomain,
          isRootDomain: !subdomain,
          modalData: dnsData
        })
        return
      }
      if (!!validate) {
        formik.setFieldValue(`validationFailed`, true)
        setLoading(false)
      } else {
        setLoading(false)
        onFail(domainValidateErrorMessage)
      }
    }
  }

  const onRunDiagnostics = async () => {
    setLoading(true)
    setDNSRecordsDiagnostics(null)
    try {
      const validateOrRetry = (validate && JSON.parse(validate)) || (retry && JSON.parse(retry))

      // Skip diagnostics for Enom domains and directly validate
      if (validateOrRetry?.isEnomDomain) {
        handleRevalidate()
        return
      }

      const diagnostics = await api.post(`${API_ROOT}/v3/custom-domain/diagnostics`, {
        domain,
        dnsData: validateOrRetry
      })
      const results = diagnostics?.data

      if (isDiagnosticsMismatch(results)) {
        setDNSRecordsDiagnostics({ ...results, records: results?.records?.filter((r) => !r.matches) })
        setLoading(false)
      } else if (!isDiagnosticsMismatch(results) && results?.message) {
        setDNSRecordsDiagnostics({ message: results?.message, success: results?.success })
        setIsDiagnosticsError(true)
        setLoading(false)
      } else {
        handleRevalidate()
      }
    } catch (error) {
      setIsDiagnosticsError(true)
      setDNSRecordsDiagnostics({ error })
      setLoading(false)
    }
  }

  const getData = () => {
    let data = null
    if (!!retry) {
      data = JSON.parse(retry)
      formik.setFieldValue('validationFailed', true)
    } else if (!!viewRecords) {
      data = JSON.parse(viewRecords)
    }
    if (data) {
      const { domain, subdomain } = data
      formik.setFieldValue('dnsAddress.domain', domain)
      formik.setFieldValue('dnsRecords.data', data)
      formik.setFieldValue('dnsAddress.subdomain', subdomain)
    }
  }

  useEffect(() => {
    getData()
  }, [viewRecords, retry])

  const getComponentState = (values) => {
    if (!values?.isSetUpComplete) {
      if (values?.validationFailed) {
        return 'VALIDATION_FAILED'
      }
      return 'WAITING_FOR_VALIDATION'
    }

    return 'DOMAIN_SETUP_COMPLETE'
  }

  const componentState = getComponentState(values)
  switch (componentState) {
    case 'VALIDATION_FAILED':
      return (
        <div className={styles.container}>
          <ValidationFailed
            loading={loading}
            validate={validate}
            onValidate={handleRevalidate}
            values={values}
            onSelectEntri={onSelectEntri}
            onFail={onFail}
            isDiagnosticsMismatch={isDiagnosticsMismatch}
            DNSRecordsDiagnostics={DNSRecordsDiagnostics}
            onRunDiagnostics={onRunDiagnostics}
            isDiagnosticsError={isDiagnosticsError}
          />
        </div>
      )

    case 'DOMAIN_SETUP_COMPLETE':
      return (
        <>
          <div className={styles.container}>
            <div className={styles.subHeading}>
              <div className={styles.title}>{SUCCESS.COMPLETE_TITLE}</div>
              <div className={styles.subTitle}>
                {isPublishToCustomDomain ? SUCCESS.COMPLETE_DESC_PUBLISH_TO_CUSTOM_DOMAIN : SUCCESS.COMPLETE_DESC}
              </div>
              {!isPublishToCustomDomain && (
                <div className={styles.actionOptions}>
                  <p className={styles.actionPrompt}>What would you like to do next?</p>
                  <div className={styles.actionButtons}>
                    <Button
                      variant="outline-primary"
                      size="medium"
                      onClick={handlePublishExistingProject}
                      className={styles.actionButton}
                    >
                      Publish existing project to custom domain
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="medium"
                      onClick={handleUpload}
                      className={styles.actionButton}
                    >
                      Upload new project
                    </Button>
                  </div>
                </div>
              )}
              <div className={styles.btnContainer}>
                <Button
                  size="small"
                  variant="outline-primary"
                  showLeftArrow={true}
                  showArrow={false}
                  leftArrowClass={styles.backArrow}
                  onClick={onPrevious}
                  disabled={true}
                >
                  Back
                </Button>
                <Button onClick={submit} size="small" className={styles.forwardButton}>
                  Finish
                </Button>
              </div>
            </div>
          </div>

          {/* Project Selector Modal - First step: Select which project to publish */}
          {showProjectSelectorModal && (
            <ProjectSelectorModal
              show={showProjectSelectorModal}
              onHide={handleCloseProjectSelector}
              onSelectProject={handleProjectSelected}
              customDomain={subdomain ? `${subdomain}.${domain}` : domain}
            />
          )}

          {/* Custom Domain Selector Modal - Second step: Select subdomain for the project */}
          {showPublishModal && selectedProject && (
            <CustomDomainSelectorModal
              show={showPublishModal}
              onHide={() => {
                setShowPublishModal(false)
                setSelectedProject(null)
                // Revert URL back to /manage
                history.replace({ pathname: '/manage' })
              }}
              validatedDomains={validatedCustomDomains}
              site={selectedProject}
              onAddNewDomain={() => {
                setShowPublishModal(false)
                setSelectedProject(null)
                // Revert URL back to /manage
                history.replace({ pathname: '/manage' })
                // Note: User can't add new domain from here as we're already in domain setup
              }}
              onUpdate={(site) => {
                setShowPublishModal(false)
                setSelectedProject(null)
                // Revert URL back to /manage
                history.replace({ pathname: '/manage' })
                // Site was published successfully, refresh user data
                refreshProfile()
              }}
            />
          )}
        </>
      )

    case 'WAITING_FOR_VALIDATION':
    default:
      if (dnsData?.isPhpDomain) {
        return (
          <div className={styles.container}>
            <div className={styles.subHeading}>
              <div className={styles.title}>{SUCCESS.COMPLETE_TITLE}</div>
              <div className={styles.subTitle}>{SUCCESS.COMPLETE_DESC_PUBLISH_TO_CUSTOM_DOMAIN}</div>
            </div>
            <div className={`${styles.btnContainer} ${styles.right}`}>
              <Button onClick={submit} size="small" className={styles.forwardButton}>
                Done
              </Button>
            </div>
          </div>
        )
      }

      return (
        <div className={styles.container}>
          <div className={styles.subHeading}>
            <div className={styles.title}>{SUCCESS.SUBHEADING}</div>
            <div className={styles.subTitle}>{SUCCESS.DESCRIPTION}</div>
            <Timer creationTime={createdTime} formik={formik} />
            {!!viewRecords && (
              <Accordion className={styles.accordion} flush>
                <Accordion.Item eventKey={1}>
                  <Accordion.Header>
                    <span className="font-16">Show DNS records</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className={styles.recordsCon}>
                      <DNSTable data={dnsData} domain={domain} />
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}
          </div>
          <div className={`${styles.btnContainer} ${!!validate && styles.right}`}>
            {!!!validate && (
              <Button
                variant="outline-primary"
                onClick={onPrevious}
                size="small"
                showLeftArrow={true}
                showArrow={false}
                leftArrowClass={styles.backArrow}
                disabled={true}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleRevalidate}
              size="small"
              disabled={!values.expired}
              className={styles.forwardButton}
              loading={loading}
            >
              Validate
            </Button>
          </div>
        </div>
      )
  }
}

export default Success

const ValidationFailed = ({
  validate,
  loading,
  onValidate,
  values,
  onSelectEntri,
  onFail,
  isDiagnosticsMismatch,
  DNSRecordsDiagnostics,
  onRunDiagnostics,
  isDiagnosticsError
}) => {
  const [loadingEntri, setLoadingEntri] = useState(false)
  const dnsData = values.dnsRecords.data
  const isOwner = !!(values.dnsAddress.selected === 'yes')
  const domain_value = values.dnsAddress.domain

  const VALIDATE_RECORDS_CONSTANTS = useMemo(
    () => (isDiagnosticsMismatch(DNSRecordsDiagnostics) ? VALIDATE_RECORDS_DIAGNOSTICS : VALIDATE_RECORDS),
    [DNSRecordsDiagnostics]
  )

  useEffect(() => {
    if (!DNSRecordsDiagnostics && !loading && dnsData) {
      onRunDiagnostics()
    }
  }, [])

  const handleRetryEntri = async () => {
    const payload = {
      domain: domain_value,
      owner: isOwner,
      replaceWWW: dnsData?.isRootDomain
    }
    setLoadingEntri(true)
    try {
      const {
        data: { domain, token, cert, cfName }
      } = await autoConnectDomainAdd(payload)
      onSelectEntri(domain, token, cert, cfName, dnsData?.isRootDomain)
      setLoadingEntri(false)
    } catch (error) {
      setLoadingEntri(false)
      onFail(domainErrorMessage)
    }
  }

  return (
    <>
      {loading ? (
        <>
          <div className={styles.subHeading}>
            <div className={styles.title}>{VALIDATE_RECORDS_CONSTANTS.UPLOADING}</div>
            <div className={styles.subTitle}>{VALIDATE_RECORDS_CONSTANTS.UPLOADING_DESC}</div>
          </div>

          <div className={`${styles.btnContainer} ${styles.center}`}>
            <Spinner animation="border" />
          </div>
        </>
      ) : (
        <>
          <div className={styles.subHeading}>
            <div className={styles.title}>{VALIDATE_RECORDS_CONSTANTS.FAILED}</div>
            <div className={styles.subTitle}>{VALIDATE_RECORDS_CONSTANTS.DESCRIPTION}</div>
          </div>
          <div className={styles.recordsCon}>
            <DNSTable data={dnsData} domain={domain_value} />
          </div>
          <div className={styles.retryEntri}>
            {!isDiagnosticsError && (
              <Button onClick={handleRetryEntri} size="sm" variant="outline-secondary" disabled={loading}>
                {loadingEntri ? <Spinner animation="border" size="sm" /> : 'Retry adding DNS records automatically'}
              </Button>
            )}
          </div>
          <div className={`${styles.btnContainerRetry} ${styles.right}`}>
            {!isDiagnosticsError && (
              <Button className="w-100" onClick={onRunDiagnostics} variant="primary" disabled={loadingEntri}>
                Re-validate
              </Button>
            )}
          </div>
        </>
      )}
    </>
  )
}
